import React, { createContext, useContext, useEffect, useState } from "react";
import { apiRequest } from "../api/client";
import { useAuth } from "./AuthContext";
import Loading from "../components/Loading";

const ConfigContext = createContext(null);

const DEFAULT_FEATURES = {
  enableTagSelector: false,
  enableAssigneeSelection: true,
  forceLoginOnSelectTemplate: false,
  forceLoginOnDeleteTodo: false,
  enableCheckList: true,
  enableUploadImage: true,
};

export const ConfigProvider = ({ children }) => {
  const { authStatus, currentUserKey } = useAuth();
  const [config, setConfig] = useState({
    features: DEFAULT_FEATURES,
    featuresLoaded: true,
    loadedForUserKey: null,
  });

  useEffect(() => {
    let cancelled = false;

    const loadFeatures = async () => {
      if (authStatus !== "authenticated" || !currentUserKey) {
        if (!cancelled) {
          setConfig({
            features: DEFAULT_FEATURES,
            featuresLoaded: true,
            loadedForUserKey: null,
          });
        }
        return;
      }

      if (!cancelled) {
        setConfig((prev) => ({
          ...prev,
          featuresLoaded: false,
          loadedForUserKey: null,
        }));
      }

      try {
        const response = await apiRequest("/api/v1/tenant/config");
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        if (!cancelled) {
          setConfig({
            features: { ...DEFAULT_FEATURES, ...data.features },
            featuresLoaded: true,
            loadedForUserKey: currentUserKey,
          });
        }
      } catch (err) {
        console.warn("Failed to load tenant config:", err);
        if (!cancelled) {
          if (err?.message === "Unauthorized") {
            return;
          }
          window.location.replace("/error?reason=server-error");
        }
      }
    };

    loadFeatures();
    return () => {
      cancelled = true;
    };
  }, [authStatus, currentUserKey]);

  return (
    <ConfigContext.Provider value={config}>
      {authStatus !== "authenticated" ||
      (config.featuresLoaded && config.loadedForUserKey === currentUserKey) ? (
        children
      ) : (
        <Loading />
      )}
    </ConfigContext.Provider>
  );
};

export const useConfig = () => {
  const config = useContext(ConfigContext);
  if (!config) {
    throw new Error("useConfig must be used within a ConfigProvider");
  }
  return config;
};
